// The Oversight rules vocabulary, app-side (owner, 2026-09-15).
//
// The vocabulary — scopes, fields, operators, their clause words and the rules
// that pair them — is `@ap/shared`'s, re-exported: the same module the server
// validates a rule against (code review D-08, 2026-10-03). What stays here is
// the editor's own: the fields offered under a scope, a blank row, and the
// comparisons the Save button needs.
//
// The labels are clauses of the sentence a rule reads as
// (`oversightRuleSentence.ts`), which is why they are lower case and why the
// scopes carry a curly apostrophe.

import {
  OVERSIGHT_RULE_FIELDS,
  OVERSIGHT_FIELD_TYPE,
  fieldAllowedAtScope,
} from '@ap/shared'
import { REQUIRED_APPROVAL_FIELD, type ApprovalRequirement } from '@ap/shared'
import type { OversightRuleCondition, OversightRuleField, OversightRuleScope, RequiredApprovals } from '../api/types'

export {
  MAX_OVERSIGHT_CONDITIONS_PER_RULE,
  OPERATORS_BY_TYPE,
  OVERSIGHT_FIELD_LABEL,
  OVERSIGHT_FIELD_SCOPE,
  OVERSIGHT_FIELD_TYPE,
  OVERSIGHT_OPERATOR_CLAUSE,
  OVERSIGHT_OPERATOR_LABEL,
  OVERSIGHT_RULE_FIELDS,
  OVERSIGHT_RULE_OPERATORS,
  OVERSIGHT_RULE_SCOPES,
  OVERSIGHT_SCOPE_CLAUSE,
  fieldAllowedAtScope,
  isLineScope,
  operatorsFor,
} from '@ap/shared'

/** The fields offered under a scope, in declaration order. */
export function fieldsForScope(scope: OversightRuleScope): readonly OversightRuleField[] {
  return OVERSIGHT_RULE_FIELDS.filter((field) => fieldAllowedAtScope(field, scope))
}

/** A condition as written, with no resolved labels. */
export type OversightRuleConditionDraft = Omit<OversightRuleCondition, 'value_labels'>

/** A blank condition row. A new rule starts with one, and it is not saveable
    until a scope, a field, an operator and a value are all answered. */
export function blankCondition(position: number): OversightRuleConditionDraft {
  return {
    position,
    scope: 'invoice',
    field: 'total',
    operator: 'is_at_least',
    value_ids: null,
    value_text: null,
    value_number: null,
    value_date: null,
  }
}

/**
 * WHETHER TWO CONDITION SETS SAY THE SAME THING (QA wave B review, B-05), so
 * an unchanged Save on Rules -> Oversight Approval writes nothing and says
 * `NOTHING_CHANGED`, as every other editor does. Compared in sentence order,
 * field by field; the several values of one entity condition are a set, so
 * their order does not count; a blank value and null are one.
 *
 * Values are compared TRIMMED because that is how they are stored: the route
 * trims `value_text`, `value_number` and `value_date` before writing
 * (`backend/src/http/routes/oversightRules.ts`), and the mock does the same. A
 * save that only adds spaces would store what is already there, so it is
 * "nothing changed", not a write (second review of QA wave B, 2026-09-25).
 */
export function sameConditions(
  a: readonly OversightRuleConditionDraft[],
  b: readonly OversightRuleConditionDraft[],
): boolean {
  if (a.length !== b.length) return false
  const ordered = (list: readonly OversightRuleConditionDraft[]) =>
    [...list].sort((x, y) => x.position - y.position)
  const ids = (v: string[] | null) => [...(v ?? [])].sort().join('\u0000')
  const text = (v: string | null | undefined) => (v ?? '').trim()
  const left = ordered(a)
  const right = ordered(b)
  return left.every((x, i) => {
    const y = right[i]!
    return (
      x.scope === y.scope &&
      x.field === y.field &&
      x.operator === y.operator &&
      ids(x.value_ids) === ids(y.value_ids) &&
      text(x.value_text) === text(y.value_text) &&
      text(x.value_number) === text(y.value_number) &&
      text(x.value_date) === text(y.value_date)
    )
  })
}

/** Whether a condition carries the value its field's type needs. */
export function conditionIsComplete(condition: OversightRuleConditionDraft): boolean {
  switch (OVERSIGHT_FIELD_TYPE[condition.field]) {
    case 'entity':
      return (condition.value_ids?.length ?? 0) > 0
    case 'text':
      return (condition.value_text ?? '').trim() !== ''
    case 'money':
    case 'number':
      return /^-?\d{1,10}(\.\d{1,4})?$/.test((condition.value_number ?? '').trim())
    default:
      return /^\d{4}-\d{2}-\d{2}$/.test((condition.value_date ?? '').trim())
  }
}

/**
 * One toggle flipped, the form's rule applied (owner, 2026-10-09 — D-336):
 * "Since we do not want invoices to stop at both Controller Approval and
 * Manager Approval, even though we made the pipeline look linear, make this
 * form toggle off Manager Approval when Controller Approval is toggled on, and
 * vice versa." Turning either OFF changes nothing else.
 */
export function toggleRequiredApproval(
  current: RequiredApprovals,
  key: ApprovalRequirement,
  on: boolean,
): RequiredApprovals {
  const next = { ...current, [REQUIRED_APPROVAL_FIELD[key]]: on }
  if (on && key === 'controller') next.requires_manager = false
  if (on && key === 'manager') next.requires_controller = false
  return next
}
